/**
 * Pillar 6 — Inbox Item Detail.
 *
 * Right pane of the Foreman Inbox. Shows the full operational record:
 *   - title, project, sheet, sender, severity
 *   - three big action buttons: Acknowledge / Needs Clarification / Field Conflict
 *   - Complete (terminal)
 *   - clarification thread (purpose-built)
 *   - audit trail timeline
 *
 * Performance budgets: detail open < 500ms, acknowledge < 250ms.
 * Touch-first: large tap targets, no hover dependencies.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircle2, MessageSquareWarning, AlertTriangle, Send,
  X, Loader2, ShieldAlert, Eye, ListChecks, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";
import StatusPill from "@/components/inbox/StatusPill";
import ImpactChip from "@/components/war_room/ImpactChip";
import QuestionThread from "@/components/inbox/QuestionThread";
import StoryDrawer from "@/components/stories/StoryDrawer";


export default function InboxItemDetail({ itemId, onChanged, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [perf, setPerf] = useState({ open_ms: null, ack_ms: null });
  const [clarifying, setClarifying] = useState(false);
  const [clarifyText, setClarifyText] = useState("");
  const [conflicting, setConflicting] = useState(false);
  const [conflictText, setConflictText] = useState("");
  const [showStory, setShowStory] = useState(false);
  const tOpen = useRef(performance.now());

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const r = await api.get(`/foreman/inbox/${itemId}`);
      setData(r.data);
      setPerf((p) => p.open_ms == null ? { ...p, open_ms: Math.round(performance.now() - t0) } : p);
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Couldn't open item.");
    } finally {
      setLoading(false);
    }
  }, [itemId]);
  useEffect(() => { load(); }, [load]);

  const item = data?.item;

  const doAck = async () => {
    setSubmitting(true);
    const t0 = performance.now();
    try {
      const r = await api.post(`/foreman/inbox/${itemId}/ack`, {});
      setData((d) => ({ ...(d || {}), item: r.data.item }));
      setPerf((p) => ({ ...p, ack_ms: Math.round(performance.now() - t0) }));
      confirm.raw?.success?.(`Acknowledged · ${Math.round(performance.now() - t0)} ms`);
      onChanged?.();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Acknowledge failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const doNeedsClarification = async () => {
    if (!clarifyText.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/foreman/inbox/${itemId}/needs-clarification`, {
        question: clarifyText.trim(),
      });
      setData((d) => ({ ...(d || {}), item: r.data.item }));
      setClarifyText("");
      setClarifying(false);
      confirm.raw?.success?.("Question sent");
      await load();
      onChanged?.();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Couldn't post.");
    } finally {
      setSubmitting(false);
    }
  };

  const doFieldConflict = async () => {
    if (!conflictText.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/foreman/inbox/${itemId}/field-conflict`, {
        question: conflictText.trim(),
      });
      setData((d) => ({ ...(d || {}), item: r.data.item }));
      setConflictText("");
      setConflicting(false);
      confirm.raw?.success?.("Field conflict raised");
      await load();
      onChanged?.();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Couldn't raise.");
    } finally {
      setSubmitting(false);
    }
  };

  const doComplete = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/foreman/inbox/${itemId}/complete`, {});
      setData((d) => ({ ...(d || {}), item: r.data.item }));
      confirm.raw?.success?.("Marked Complete");
      onChanged?.();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Complete failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="p-8 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="foreman-inbox-detail-loading">
        <Loader2 className="animate-spin" size={14} /> Opening…
      </div>
    );
  }
  if (!item) return null;

  const terminal = item.status === "complete";

  return (
    <div className="p-4 max-w-3xl mx-auto" data-testid="foreman-inbox-detail">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <StatusPill status={item.status} testid="foreman-inbox-detail-status" />
            <ImpactChip severity={item.priority || "moderate"} testid="foreman-inbox-detail-priority" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">
              {item.sheet_number} · REV {item.current_revision}
            </span>
          </div>
          <h1 className="text-xl font-semibold text-[var(--fop-text)]" data-testid="foreman-inbox-detail-title">
            {item.title || `Revision ${item.sheet_number} REV ${item.current_revision}`}
          </h1>
          <div className="text-xs text-[var(--fop-text-3)] mt-1 flex items-center gap-1.5">
            <span>
              Sent by {item.sender_name || "Superintendent"}
              {item.sent_at ? ` · ${new Date(item.sent_at).toLocaleString()}` : ""}
            </span>
            {perf.open_ms != null && (
              <span
                className="cursor-default inline-flex"
                title={`Open ${perf.open_ms}ms · Acknowledge ${perf.ack_ms ?? "—"}ms`}
                data-testid="foreman-inbox-detail-perf"
              >
                <Clock size={11} />
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
          aria-label="Close"
          data-testid="foreman-inbox-detail-close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Action buttons — big, glove-friendly */}
      {!terminal && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          <ActionButton
            icon={CheckCircle2} label="Acknowledge" colour="success"
            onClick={doAck} busy={submitting}
            testid="foreman-inbox-action-ack"
          />
          <ActionButton
            icon={MessageSquareWarning} label="Needs Clarification" colour="warning"
            onClick={() => setClarifying((s) => !s)} busy={submitting}
            testid="foreman-inbox-action-clarify"
            active={clarifying}
          />
          <ActionButton
            icon={ShieldAlert} label="Field Conflict" colour="danger"
            onClick={() => setConflicting((s) => !s)} busy={submitting}
            testid="foreman-inbox-action-conflict"
            active={conflicting}
          />
        </div>
      )}

      {/* Track 2B — See whole story. One tap to "What Happened Here". */}
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowStory(true)}
          className="inline-flex items-center gap-1.5 text-[12px] px-3 py-2 rounded border border-[var(--fop-primary)]/40 bg-[var(--fop-primary-soft)]/40 text-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)]/70"
          data-testid="foreman-inbox-see-story"
        >
          <ListChecks size={12} /> See whole story
        </button>
      </div>
      <StoryDrawer
        open={showStory}
        inboxItemId={itemId}
        onClose={() => setShowStory(false)}
      />

      {/* Clarification entry box */}
      {clarifying && (
        <div className="mb-4 p-3 border border-[var(--fop-warning)]/40 rounded bg-[var(--fop-warning-soft, rgba(245,158,11,0.08))]" data-testid="foreman-inbox-clarify-box">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">
            What's the question?
          </div>
          <textarea
            value={clarifyText}
            onChange={(e) => setClarifyText(e.target.value)}
            placeholder="Plain English — what do you need the super to clarify?"
            className="w-full px-3 py-2 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text)] text-sm"
            rows={3}
            data-testid="foreman-inbox-clarify-input"
            maxLength={1500}
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={doNeedsClarification}
              disabled={!clarifyText.trim() || submitting}
              className="btn btn-primary text-[12px] inline-flex items-center gap-1"
              data-testid="foreman-inbox-clarify-submit"
            >
              <Send size={12} /> Send question
            </button>
            <button
              onClick={() => { setClarifyText(""); setClarifying(false); }}
              className="text-[12px] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Field conflict entry box */}
      {conflicting && (
        <div className="mb-4 p-3 border border-[var(--fop-danger,#dc2626)]/40 rounded bg-[var(--fop-danger-soft, rgba(220,38,38,0.07))]" data-testid="foreman-inbox-conflict-box">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">
            What's the field conflict?
          </div>
          <textarea
            value={conflictText}
            onChange={(e) => setConflictText(e.target.value)}
            placeholder="What you see in the field that doesn't match the drawing."
            className="w-full px-3 py-2 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text)] text-sm"
            rows={3}
            data-testid="foreman-inbox-conflict-input"
            maxLength={1500}
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={doFieldConflict}
              disabled={!conflictText.trim() || submitting}
              className="btn btn-primary text-[12px] inline-flex items-center gap-1"
              data-testid="foreman-inbox-conflict-submit"
            >
              <Send size={12} /> Raise conflict
            </button>
            <button
              onClick={() => { setConflictText(""); setConflicting(false); }}
              className="text-[12px] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Complete button — terminal */}
      {!terminal && (item.status === "acknowledged" || item.viewed_at) && (
        <button
          onClick={doComplete}
          disabled={submitting}
          className="mb-4 w-full py-3 rounded border border-[var(--fop-success)]/60 bg-[var(--fop-success-soft, rgba(34,197,94,0.08))] text-[var(--fop-text)] hover:brightness-110 inline-flex items-center justify-center gap-2 text-sm font-semibold"
          data-testid="foreman-inbox-action-complete"
        >
          <ListChecks size={16} /> Mark Complete
        </button>
      )}

      {/* Clarification thread */}
      <QuestionThread itemId={itemId} questions={data?.questions || []} onPosted={() => load()} />

      {/* Audit timeline */}
      <div className="mt-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-2">
          History
        </div>
        <ol className="space-y-1.5">
          {(data?.events || []).map((e) => (
            <li
              key={e.id}
              className="flex items-start gap-2 text-[12px] text-[var(--fop-text-2)]"
              data-testid={`foreman-inbox-event-${e.kind}`}
            >
              <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] w-32 shrink-0">
                {e.kind.replace(/_/g, " ")}
              </span>
              <span className="flex-1">
                <span className="font-medium">{e.actor_name}</span>
                {e.comment ? ` — "${e.comment}"` : ""}
              </span>
              <span className="text-[10px] font-mono text-[var(--fop-text-3)] shrink-0">
                {new Date(e.created_at).toLocaleString()}
              </span>
            </li>
          ))}
          {(data?.events || []).length === 0 && (
            <li className="text-[11px] text-[var(--fop-text-3)]">No history yet.</li>
          )}
        </ol>
      </div>
    </div>
  );
}


function ActionButton({ icon: Icon, label, colour, onClick, busy, testid, active }) {
  const ring = {
    success: "border-[var(--fop-success)]/60",
    warning: "border-[var(--fop-warning)]/60",
    danger:  "border-[var(--fop-danger,#dc2626)]/60",
  }[colour] || "border-[var(--fop-border)]";
  const bg = active
    ? "bg-[var(--fop-primary-soft)]"
    : "bg-[var(--fop-surface)] hover:bg-[var(--fop-surface-2)]";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`min-h-[64px] px-3 py-2 rounded border ${ring} ${bg} flex items-center gap-2 text-sm font-semibold text-[var(--fop-text)] disabled:opacity-50 transition-colors`}
    >
      <Icon size={20} className="text-[var(--fop-text-2)]" />
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}
